<template>
  <v-dialog :model-value="modelValue" max-width="760px" persistent scrollable data-testid="tax-config-dialog" @update:model-value="$emit('update:modelValue', $event)">
    <v-card @keydown.esc="closeDialog">
      <v-card-title class="bg-primary text-white d-flex align-center pa-4">
        <v-icon class="mr-2">{{ isEditMode ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
        <div class="flex-grow-1">
          <div class="text-h6">{{ isEditMode ? 'แก้ไขกฎภาษี (Tax Rule)' : 'เพิ่มกฎภาษี (Tax Rule)' }}</div>
        </div>
        <v-btn icon variant="text" color="white" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <v-form ref="ruleForm">
        <v-alert
          v-if="submitAttempted && validationErrors.length"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
          data-testid="config-dialog-error"
        >
          <div class="font-weight-medium mb-1">กรุณาตรวจสอบข้อมูลให้ครบถ้วน</div>
          <ul class="cfg-error-list">
            <li v-for="(msg, i) in validationErrors" :key="i">{{ msg }}</li>
          </ul>
        </v-alert>

        <section class="cfg-form-section">
          <h4 class="cfg-section-title">กฎภาษี (Tax Rule)</h4>

          <v-row>
            <v-col cols="12" md="4">
              <v-select
                v-model="editableRule.category"
                :items="categoryItems"
                item-title="label"
                item-value="code"
                label="หมวด (Category)"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-shape-outline"
                :loading="loadingCategories"
                :rules="categoryRules"
                data-testid="config-category-select"
                @update:model-value="onSelectCategory"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="editableRule.rule_code"
                label="รหัสกฎ (rule_code)"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-identifier"
                placeholder="เช่น SOCSEC_33"
                :readonly="isEditMode"
                :rules="ruleCodeRules"
                data-testid="config-rulecode-input"
                @update:model-value="onRuleCodeInput"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="editableRule.type"
                :items="typeOptions"
                label="ชนิดข้อมูล (Type)"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-format-list-bulleted-type"
                data-testid="config-type-select"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="editableRule.label"
                label="ชื่อที่แสดง (Label)"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-format-title"
                :rules="labelRules"
                data-testid="config-label-th"
              />
            </v-col>
          </v-row>
        </section>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4 d-flex align-center flex-wrap ga-2">
        <div class="cfg-stage-hint d-flex align-center">
          <v-icon size="16" class="mr-1">mdi-information-outline</v-icon>
          <span>การเปลี่ยนแปลงจะถูกพักไว้ — กด Save ที่หน้าหลักเพื่อบันทึกถาวร</span>
        </div>
        <v-spacer />
        <div class="d-flex ga-2">
          <v-btn data-testid="config-dialog-cancel-btn" variant="outlined" @click="closeDialog">
            <v-icon start>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            data-testid="config-dialog-submit-btn"
            color="primary"
            variant="elevated"
            @click="submit"
          >
            <v-icon start>{{ isEditMode ? 'mdi-check' : 'mdi-playlist-plus' }}</v-icon>
            {{ isEditMode ? 'บันทึกการแก้ไข' : 'เพิ่มในรายการ' }}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
function createEmptyRule() {
  return {
    rule_code: '',
    category: '',
    category_label: '',
    label: '',
    type: 'number',
    active: true,
  };
}

export default {
  name: 'TaxConfigDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    initialRule: { type: Object, default: null },
    existingRules: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue', 'submit'],
  data() {
    return {
      editableRule: createEmptyRule(),
      submitAttempted: false,
      categoryItems: [],
      loadingCategories: false,
      typeOptions: ['number', 'text', 'boolean'],
    };
  },
  computed: {
    isEditMode() {
      return Boolean(this.initialRule && this.initialRule.rule_code);
    },
    isDuplicateRuleCode() {
      const code = String(this.editableRule.rule_code || '').trim();
      if (!code) return false;
      return this.existingRules.some((row) => String(row.rule_code || '').trim() === code);
    },
    // Single source of truth for "is this rule valid" — drives the error summary
    // and the inline field rules below. Returns human-readable messages.
    validationErrors() {
      const e = [];
      const r = this.editableRule;
      if (!String(r.rule_code || '').trim()) e.push('ต้องกรอก rule code');
      else if (!this.isEditMode && this.isDuplicateRuleCode) e.push(`rule code "${String(r.rule_code).trim()}" มีอยู่แล้ว`);
      if (!r.category) e.push('ต้องเลือก category');
      if (!String(r.label || '').trim()) e.push('ต้องกรอก label (TH)');
      return e;
    },
    canSubmit() {
      return this.validationErrors.length === 0;
    },
    // ── inline field rules (Vuetify :rules) ──
    ruleCodeRules() {
      return [
        (v) => !!String(v || '').trim() || 'ต้องกรอก rule code',
        (v) => this.isEditMode
          || !this.existingRules.some((row) => String(row.rule_code || '').trim() === String(v || '').trim())
          || 'rule code นี้มีอยู่แล้ว',
      ];
    },
    categoryRules() {
      return [(v) => !!v || 'ต้องเลือก category'];
    },
    labelRules() {
      return [(v) => !!String(v || '').trim() || 'ต้องกรอก label (TH)'];
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.resetForm();
        this.loadCategories();
      }
    },
  },
  methods: {
    resetForm() {
      const base = createEmptyRule();
      this.editableRule = this.initialRule
        ? { ...base, ...JSON.parse(JSON.stringify(this.initialRule)) }
        : base;
      this.submitAttempted = false;
      this.$nextTick(() => this.$refs.ruleForm && this.$refs.ruleForm.resetValidation());
    },
    loadCategories() {
      if (!this.$call || typeof this.$call.GetTaxCategories !== 'function') {
        this.categoryItems = [];
        return;
      }
      this.loadingCategories = true;
      this.$call.GetTaxCategories(
        (response) => {
          if (response && response.status === 0 && Array.isArray(response.data)) {
            this.categoryItems = response.data
              .map((item) => ({
                code: item.categoryCode ?? item.CategoryCode ?? '',
                label: `${item.categoryCode ?? item.CategoryCode ?? ''} - ${item.labelTh ?? item.LabelTh ?? ''}`,
                labelTh: item.labelTh ?? item.LabelTh ?? '',
                sortOrder: item.sortOrder ?? item.SortOrder ?? 0,
                isActive: item.isActive ?? item.IsActive ?? false,
              }))
              .filter((item) => item.code && item.isActive)
              .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
          } else {
            this.categoryItems = [];
          }
          this.loadingCategories = false;
        },
        (error) => {
          console.error('Failed to load tax categories:', error);
          this.categoryItems = [];
          this.loadingCategories = false;
        },
      );
    },
    onSelectCategory(code) {
      const found = this.categoryItems.find((item) => item.code === code);
      this.editableRule.category = code;
      this.editableRule.category_label = found ? found.labelTh : code;
    },
    onRuleCodeInput(value) {
      // 🆕 R6 — uppercase on input so it matches the UPPER_SNAKE RuleCode applied at
      // bind time (a lower-case entry would otherwise mismatch existing rule codes).
      this.editableRule.rule_code = String(value || '').replace(/\s+/g, '').toUpperCase();
    },
    closeDialog() {
      this.$emit('update:modelValue', false);
    },
    async submit() {
      this.submitAttempted = true;
      // Paint inline field errors too (covers conditionally-mounted numeric fields).
      if (this.$refs.ruleForm && this.$refs.ruleForm.validate) {
        await this.$refs.ruleForm.validate();
      }
      if (!this.canSubmit) return;
      this.$emit('submit', JSON.parse(JSON.stringify(this.editableRule)));
    },
  },
};
</script>

<style scoped>
.cfg-section-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.cfg-form-section {
  background: #fff;
}

.cfg-error-list {
  margin: 0;
  padding-left: 18px;
  font-size: 0.85rem;
}

.cfg-stage-hint {
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 1.3;
}
</style>
